import React, { useCallback } from 'react'
import { PropTypes } from 'prop-types'
import { useMutation } from '@apollo/client'
import useCurrentUser from 'lib/hooks/useCurrentUser'
import { updateEventCacheMergeAndModify } from 'lib/utils/store/event'

import ActionLogic from 'components/Util/ActionLogic'

import vehiclePricePrediction from 'graphql/vehicle/vehiclePricePrediction.gql'
import vehiclePricePredictionEvent from 'graphql/vehicle/createVehiclePricePredictionEvent.gql'
import getVehicleProfileView from 'graphql/view/getVehicleProfileView.gql'
import getTasks from 'graphql/task/getTasks.gql'

const VehiclePriceEstimateLogic = (props) => {
    const currentUser = useCurrentUser()

    const [vehiclePricePredictionMutation] = useMutation(vehiclePricePrediction)
    const [vehiclePricePredictionEventMutation] = useMutation(vehiclePricePredictionEvent)

    const getPricePrediction = useCallback(async (actionLogic, vehicleId, mileage, addToTimeline = false) => {
        return actionLogic.callMutation(vehiclePricePredictionMutation, {
            variables: {
                PricePredictionInput: {
                    mileage: mileage,
                },
                vehicleId: vehicleId,
                addToTimeline: addToTimeline,
            },
            update: (cache, { data }) => {
                const events = data.pricePrediction.pricePredictionEvent

                if (events) {
                    updateEventCacheMergeAndModify(cache, events)
                }
            },
            refetchQueries: [{
                query: getVehicleProfileView,
                variables: {
                    vehicleId: vehicleId,
                    userId: currentUser.me.id,
                }
            }],
            awaitRefetchQueries: true,
        }).then((result) => {
            actionLogic.asyncSetState({
                pricePredictionResult: result,
                pricePredictionInputValues: {
                    mileage: mileage,
                    vehicleId: vehicleId,
                },
            })

            return Promise.resolve(result)
        })
    }, [currentUser, vehiclePricePredictionMutation])

    const createVehiclePricePredictionEvent = useCallback((actionLogic, vehicleId) => {
        return actionLogic.callMutation(vehiclePricePredictionEventMutation, {
            variables: {
                vehicleId: parseInt(vehicleId),
            },
            update: (cache, { data }) => {
                const events = data.createPredictionEvent

                if (events) {
                    updateEventCacheMergeAndModify(cache, events)
                }
            },
            refetchQueries: [{
                query: getTasks,
                variables: {
                    vehicleId: vehicleId,
                }
            }],
            awaitRefetchQueries: true,
        })
    }, [vehiclePricePredictionEventMutation])

    return (
        <ActionLogic
            initState={{
                pricePredictionInputValues: null,
                pricePredictionResult: null,
            }}
        >
            {(actionLogic) => {
                return props.children({
                    ...actionLogic,
                    getPricePrediction: (...args) => getPricePrediction(actionLogic, ...args),
                    createVehiclePricePredictionEvent: (...args) => createVehiclePricePredictionEvent(actionLogic, ...args),
                })
            }}
        </ActionLogic>
    )
}

VehiclePriceEstimateLogic.propTypes = {
    children: PropTypes.func.isRequired,
}

export default VehiclePriceEstimateLogic
